// Internal-note attachment upload helper. Mirrors `assist-attachments.ts`
// in shape but targets the `note-attachments` Supabase Storage bucket
// (migration 050). Used by NotesPanel's composer.
//
// RLS gating (storage policy `team_uploads_note_attachments` in 050):
// only active team members can write. The bucket is public-read for
// the same reason ticket-attachments + assist-attachments are: notes
// are accessed only by authenticated team members and the URL is
// effectively a per-file capability token (random paths).

import type { SupabaseClient } from '@supabase/supabase-js';
import type { NoteAttachment } from '@/lib/queries';

export const NOTE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB / file
export const NOTE_ATTACHMENT_MAX_PER_NOTE = 5;

function sanitizeFilename(name: string): string {
  const cleaned = name
    .replace(/[\\/]+/g, '_')
    .replace(/[^A-Za-z0-9._\-+ ()]/g, '_')
    .replace(/\s+/g, '_');
  return cleaned.slice(0, 80) || 'file';
}

export interface NoteUploadFailure {
  filename: string;
  reason: string;
}

export interface NoteUploadResult {
  attachments: NoteAttachment[];
  failures: NoteUploadFailure[];
}

/** Upload a batch of files into the per-ticket folder. Per-file errors
 *  (oversize, network, RLS) don't abort the batch — the caller renders
 *  the successful ones and toasts the failures. */
export async function uploadNoteAttachments(args: {
  client: SupabaseClient;
  ticketId: string;
  files: File[];
}): Promise<NoteUploadResult> {
  const { client, ticketId, files } = args;
  const out: NoteAttachment[] = [];
  const failures: NoteUploadFailure[] = [];

  for (const file of files) {
    if (file.size > NOTE_ATTACHMENT_MAX_BYTES) {
      failures.push({
        filename: file.name,
        reason: `over ${(NOTE_ATTACHMENT_MAX_BYTES / 1024 / 1024).toFixed(0)} MB limit`,
      });
      continue;
    }
    // Per-ticket folder so we can reason about lifetime later (e.g. a
    // future "delete the ticket → cascade cleanup" job iterates this
    // prefix). Unix-ms suffix avoids collisions when two agents upload
    // the same filename within the same second.
    const path = `${ticketId}/${Date.now()}-${sanitizeFilename(file.name)}`;
    const { error } = await client.storage
      .from('note-attachments')
      .upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false,
      });
    if (error) {
      failures.push({ filename: file.name, reason: error.message });
      continue;
    }
    const { data: pub } = client.storage.from('note-attachments').getPublicUrl(path);
    out.push({
      url: pub.publicUrl,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    });
  }

  return { attachments: out, failures };
}
